import { faBars } from "@fortawesome/pro-light-svg-icons";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Collapse, IconButton, Toolbar, useTheme } from "@mui/material";
import { memo, useEffect } from "react";
import { DragDropContext, Draggable, type DraggableProvidedDragHandleProps, Droppable } from "react-beautiful-dnd";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types";
import type { EXPERIENCE_PRIMARY_FILTER } from "@/constants";
import { useUrlParams } from "@/hooks";
import { useTabStatus } from "@/hooks/useTabStatus";
import { updateVisualBuilderData } from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import { visualBuilderDataSelector } from "@/store/selectors";
import { changeMenuItemsOrder, handleAssetMenuClick, sortMenuItems } from "@/utils/drawerUtils";

import MenuTooltip from "./MenuTooltip";

/**
 * Props interface for the AssetMenu component.
 * @property assetCategoryFilterId - ID of the currently selected asset category filter
 * @property currentMediaItemId - ID of the currently selected media item
 * @property drawerWidth - Width of the drawer in CSS units
 * @property filteredAssetCategoryMediaItems - Record of asset arrays keyed by category ID
 * @property leftDrawerOpen - Boolean indicating if the main drawer is open
 * @property tabStatus - Current tab status (e.g., HOMEBASE, PUBLIC, CUSTOMIZE)
 * @property typeFilterId - ID of the selected type filter
 * @property gridView - Boolean indicating if grid view is active
 * @property assetMenuOpen - Boolean indicating if this menu is open
 * @property propertyId - ID of the current property
 * @property experienceId - ID of the current experience
 * @property isDraggable - Optional boolean to enable/disable drag functionality
 */
interface AssetMenuProps {
  readonly assetCategoryFilterId: number | null; // Currently selected asset category
  readonly currentMediaItemId: number | null; // Currently selected media item
  readonly drawerWidth: number; // Width of the drawer
  readonly filteredAssetCategoryMediaItems: Record<number, Asset[]>; // Assets grouped by category
  readonly leftDrawerOpen: boolean; // Left drawer state
  readonly typeFilterId: number | null; // Selected content type filter
  readonly gridView: boolean; // View mode toggle
  readonly assetMenuOpen: boolean; // Menu open state
  readonly menuItems: MenuItem[]; // Menu Items for current Property or Experience
  readonly isDraggable?: boolean; // Drag-and-drop enabled flag
  readonly menuRefs: {
    setRef: (key: string) => (element: HTMLElement | null) => void;
    focusElement: (key: string) => void;
  };
  readonly primaryFilter: (typeof EXPERIENCE_PRIMARY_FILTER)[keyof typeof EXPERIENCE_PRIMARY_FILTER];
}

/**
 * Styles object containing all component styles.
 * Uses MUI's sx prop syntax and is marked as const for type inference.
 */
const styles = {
  container: {
    background: "#000000e6", // Semi-transparent black background
    bottom: 0,
    height: "100%",
    position: "absolute !important",
    top: 0,
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflow: "auto", // Enables scrolling for overflow content
    padding: "0px 16px",
    paddingBottom: "40px",
    width: "240px",
    flex: 1,
  },
  fullContainer: {
    height: "100%",
    left: 0,
    position: "absolute",
    top: 0,
    width: "100%",
  },
  item: {
    color: "#ffffff",
    display: "flex",
    fontSize: "16px",
    height: 40,
    justifyContent: "flex-start",
    textAlign: "left",
    width: "100%",
    alignItems: "center",
    padding: "0px",
    "& .showOnHoverOpacity": {
      opacity: 0,
      "&:focus": {
        opacity: 1,
      },
    },
    // Show drag handle on hover
    "&:hover": {
      "& .showOnHoverOpacity": {
        opacity: 1,
      },
    },
  },
  innerItem: {
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden", // Enables text truncation
  },
  dragHandle: {
    marginRight: "8px",
    fontSize: "12px",
    width: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "none",
  },
} as const;

/**
 * AssetMenu Component
 * Provides a collapsible menu for managing and organizing assets with drag-and-drop functionality.
 * The menu appears as a side panel and can be toggled open/closed.
 */
function AssetMenu(props: AssetMenuProps) {
  const {
    assetCategoryFilterId,
    currentMediaItemId,
    drawerWidth,
    filteredAssetCategoryMediaItems,
    gridView,
    leftDrawerOpen,
    typeFilterId,
    primaryFilter,
    assetMenuOpen,
    menuItems = [],
    isDraggable = false,
    menuRefs,
  } = props;

  // Redux and hooks setup
  const dispatch = useThunkDispatch();
  const params = useUrlParams();
  const { isTabCustomize, isTabPresent, isTabPublic, isPropertyBasedTab } = useTabStatus();
  const theme = useTheme();
  const color = (theme as unknown as { primaryColor: string }).primaryColor;

  // Get locked status from Redux store
  const {
    visualBuilderData: { locked },
  } = useSelector(visualBuilderDataSelector(params));

  // Compute menu open state based on multiple conditions
  const open = assetMenuOpen && leftDrawerOpen && assetCategoryFilterId && typeFilterId;

  // Calculate top margin based on tab status
  const marginTop = isTabPresent || isTabPublic ? 0 : "40px";

  // Filter and prepare asset list
  let list = filteredAssetCategoryMediaItems?.[assetCategoryFilterId as number] || [];
  if (typeFilterId) {
    list = list.filter((x) => x.contentTypeId === typeFilterId);
  }

  useEffect(() => {
    if (open && list.length > 0 && list[0].id !== currentMediaItemId) {
      dispatch(updateVisualBuilderData(params, { currentMediaItemId: list[0].id }, {}, true));
    }
  }, [open]);

  /**
   * Handles clicking on an asset item.
   * Delegates to handleAssetMenuClick utility function with current context.
   */
  const onClickItem = (asset: Asset) => {
    void handleAssetMenuClick({
      asset,
      assetCategoryFilterId,
      currentMediaItemId,
      gridView,
      params,
    });
  };

  /**
   * Closes the asset menu.
   * Checks for locked status in customize mode before closing.
   */
  const close = () => {
    if (locked && isTabCustomize) return;

    menuRefs.focusElement(`filter-menu-${primaryFilter}-${assetCategoryFilterId}-${typeFilterId}`);

    void dispatch(updateVisualBuilderData(params as object, { assetMenuOpen: false }, {}, true));
  };

  /**
   * Renders an individual asset item.
   * @param asset - The asset to render
   * @param handle - Optional drag handle props from react-beautiful-dnd
   */
  const renderItem = (asset: Asset, index: number, handle?: DraggableProvidedDragHandleProps | null) => {
    return (
      <MenuTooltip isMobile={isMobile} text={asset.name} section="asset-menu">
        <Button
          sx={styles.item}
          ref={menuRefs.setRef(`asset-menu-${asset.id}`)}
          onClick={() => onClickItem(asset)}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !e.shiftKey && index === list.length - 1) {
              e.preventDefault();

              return menuRefs.focusElement(`asset-menu-close`);
            }
          }}
        >
          {isDraggable && (
            <Box className="showOnHoverOpacity" sx={styles.dragHandle} {...handle}>
              <FontAwesomeIcon icon={faBars} />
            </Box>
          )}
          <Box component="span" sx={[styles.innerItem, { color: currentMediaItemId === asset.id ? color : null }]}>
            {asset.name}
          </Box>
        </Button>
      </MenuTooltip>
    );
  };

  sortMenuItems({
    parentId: typeFilterId as number,
    itemType: "media-item",
    items: list,
    menuItems,
  });

  return (
    <Box
      sx={[
        styles.container,
        { transition: "left 0.2s" },
        leftDrawerOpen ? { left: "0px" } : { left: `-${drawerWidth}px` },
      ]}
    >
      <Collapse
        orientation="horizontal"
        in={!!open}
        sx={[styles.container, { left: drawerWidth, marginTop, height: `calc(100% - ${marginTop})` }]}
      >
        <Box data-testid="asset-menu" sx={{ display: "flex", flexDirection: "column", height: "100%", flex: 1 }}>
          {/* Toolbar with close button */}
          <Box sx={{ width: drawerWidth }}>
            <Toolbar sx={{ color: "white", padding: "0px 16px !important" }}>
              <IconButton
                color="inherit"
                size="large"
                onClick={close}
                edge="start"
                data-analytics="vb-experience-second-menu-close"
                ref={menuRefs.setRef(`asset-menu-close`)}
                onKeyDown={(e) => {
                  if (e.key === "Tab" && e.shiftKey) {
                    e.preventDefault();

                    menuRefs.focusElement(`asset-menu-${list[list.length - 1].id}`);
                  }
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} color="rgb(139, 139, 139)" />
              </IconButton>
            </Toolbar>
          </Box>

          {/* Draggable list container */}
          <Box sx={styles.itemsContainer}>
            <DragDropContext
              onDragEnd={(result) => {
                void changeMenuItemsOrder({
                  result,
                  items: list,
                  menuItems,
                  itemType: "media-item",
                  parentId: typeFilterId as number,
                  params,
                  isPropertyBasedTab,
                });
              }}
            >
              <Droppable droppableId="asset-menu" isDropDisabled={!isDraggable}>
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {list.map((asset, index: number) => (
                      <Draggable
                        draggableId={asset.id.toString()}
                        index={index}
                        key={asset.id}
                        isDragDisabled={!isDraggable}
                      >
                        {(providedDraggable) => (
                          <Box {...providedDraggable.draggableProps} ref={providedDraggable.innerRef}>
                            {renderItem(asset, index, providedDraggable.dragHandleProps)}
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(AssetMenu);
